/**
 * Éditeur de figure de géométrie : une ligne par élément, comme on construit au tableau (« A = point(0, 0) »,
 * « M = midpoint(A, B) », « c = circle(O, A) »). L'aperçu se met à jour à chaque frappe ; une ligne fausse est signalée
 * avec son numéro et la dernière construction valide est gardée dans la leçon.
 */
import { useEffect, useMemo, useState } from "react";
import { GEOMETRY_PRESETS, parseConstruction, printConstruction, readGeometrySpec, initialGeometryParams, type GeometrySpec } from "@jange/shared";
import { GeometryFigure } from "@jange/lesson-player";

const HELP: [string, string][] = [
  ["view(-1, 7, -1, 6)", "cadre : x min, x max, y min, y max ; « nogrid » sans quadrillage, « axes » avec les axes"],
  ["k = slider(0.2, 0.9, 0.05, 0.5) \"Position de M\"", "curseur : min, max, pas, valeur de départ, libellé"],
  ["A = point(1, 5) draggable", "point (nombres ou expressions des curseurs : 1 - k, 3*cos(t)) ; draggable : l'élève le déplace"],
  ["I = midpoint(A, B)", "milieu ; aussi intersection(d1, d2), projection(A, d)"],
  ["s = segment(A, B) marks=2 length", "segment (codage de longueur, longueur affichée) ; line(A, B), ray(A, B)"],
  ["p = parallel(M, s) dashed", "parallèle ou perpendicular(A, d) passant par un point"],
  ["c = circle(O, 3)", "cercle de centre O, de rayon 3 ou passant par un point : circle(O, A)"],
  ["t = polygon(A, B, C) fill", "polygone (3 à 8 sommets) ; angle(B, A, C) right value ; vector(A, B) ; text(2, 4, \"(d)\")"],
  ["… amber hidden label=\"(T)\" nolabel", "options : couleur (teal, amber, ink, muted), caché, étiquette"],
];

export function GeometryEditor({ value, lang, onChange }: { value: unknown; lang: string; onChange: (spec: GeometrySpec) => void }) {
  const initial = useMemo(() => readGeometrySpec(value) ?? GEOMETRY_PRESETS[0]!.spec, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [text, setText] = useState(() => printConstruction(initial));
  const parsed = useMemo(() => parseConstruction(text), [text]);
  const [params, setParams] = useState<Record<string, number>>(() => initialGeometryParams(initial));
  useEffect(() => { if (parsed.spec) { onChange(parsed.spec); setParams((p) => ({ ...initialGeometryParams(parsed.spec!), ...p })); } }, [parsed.spec]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = parsed.spec ?? readGeometrySpec(value) ?? initial;
  const errorLines = new Set(parsed.errors.map((e) => e.line));

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="row wrap" style={{ gap: 6 }}>
        <span className="muted small" style={{ fontWeight: 700 }}>Partir d'un modèle :</span>
        {GEOMETRY_PRESETS.map((p) => (
          <button key={p.id} type="button" className="btn sm ghost" onClick={() => { if (window.confirm(`Remplacer la construction par « ${p.label} » ?`)) setText(p.text); }}>{p.label}</button>
        ))}
      </div>
      <div className="row wrap" style={{ gap: 12, alignItems: "flex-start" }}>
        <div className="stack" style={{ gap: 6, flex: "1 1 300px", minWidth: 0 }}>
          <textarea className="textarea mono" rows={Math.min(22, Math.max(8, text.split("\n").length + 1))} spellCheck={false} value={text} onChange={(e) => setText(e.target.value)} aria-label="Construction de la figure, une ligne par élément" style={{ fontSize: 13, lineHeight: 1.5 }} />
          {parsed.errors.length > 0
            ? <div className="alert warn small" role="status">{parsed.errors.slice(0, 6).map((e, i) => <div key={i}>{e.line ? `Ligne ${e.line} : ` : ""}{e.message}</div>)}<div className="muted">La dernière construction valide reste dans la leçon.</div></div>
            : <span className="small muted">{shown.elements.length} élément{shown.elements.length > 1 ? "s" : ""}{shown.sliders.length ? ` · ${shown.sliders.length} curseur${shown.sliders.length > 1 ? "s" : ""}` : ""} · dans la vidéo, chaque élément se trace dans cet ordre.</span>}
          <details>
            <summary className="small muted" style={{ cursor: "pointer" }}>Écrire une construction</summary>
            <div className="stack small" style={{ gap: 4, marginTop: 6 }}>
              {HELP.map(([code, what]) => <div key={code}><code className="mono">{code}</code> <span className="muted">— {what}</span></div>)}
            </div>
          </details>
        </div>
        <div style={{ flex: "1 1 300px", minWidth: 0, opacity: errorLines.size ? 0.6 : 1 }}>
          <GeometryFigure spec={shown} params={params} lang={lang} onParam={(n, v) => setParams((p) => ({ ...p, [n]: v }))} />
        </div>
      </div>
    </div>
  );
}
